/**
 * Gear requirements (#250): each piece of gear answers to one branch
 * discipline by its type, Martial Discipline for weapons and shields, Veil
 * Attunement for armor and jewelry, and asks for a share of it set by its
 * rarity (raised by item level within the tier).
 * Gear below its requirement is weakened, never locked: its bonuses and
 * affixes scale down by `resolveGearPotency`, its base stats don't (see
 * `buildEquipment`). First-pass numbers to tune.
 */
import { ITEM_LEVEL_MAX } from './item-affixes';
import { SKILL_CATALOG } from './skill-catalog';
import type { CharacterEquipmentType, Equipment, ItemRarity, Skill } from './types/game';

/** The branch discipline a piece of gear answers to. */
export type GearDiscipline = 'veil-attunement' | 'martial-discipline';

/** A character's standing in each discipline, 0-100%. */
export interface CharacterDisciplines {
    readonly veilAttunement: number;
    readonly martialDiscipline: number;
}

/** Where each rarity's requirement starts and the share it never reaches (the next tier's start), in %. */
export const GEAR_REQUIREMENT_TIERS: Readonly<Record<ItemRarity, { readonly start: number; readonly before: number }>> = {
    common: { start: 0, before: 0 },
    uncommon: { start: 5, before: 15 },
    rare: { start: 15, before: 30 },
    epic: { start: 30, before: 50 },
    legendary: { start: 50, before: 65 },
    mythic: { start: 65, before: 80 },
};

/** Gear never weakens below this share of its bonuses. */
export const MIN_GEAR_POTENCY = 0.25;

/** The parts of an item its requirement reads. */
export type GearRequirementSource = Pick<Equipment, 'equipmentType'> & {
    readonly instance?: Pick<NonNullable<Equipment['instance']>, 'rarity' | 'itemLevel'>;
};

export function resolveGearDiscipline(equipmentType: CharacterEquipmentType): GearDiscipline {
    return equipmentType === 'melee' || equipmentType === 'range' || equipmentType === 'shield'
        ? 'martial-discipline'
        : 'veil-attunement';
}

/** An item's discipline and the share of it it asks for, derived from its rarity and item level. */
export function resolveGearRequirement(item: GearRequirementSource): { readonly discipline: GearDiscipline; readonly percent: number } {
    const discipline = resolveGearDiscipline(item.equipmentType);
    const tier = GEAR_REQUIREMENT_TIERS[item.instance?.rarity ?? 'common'];
    const itemLevel = Math.min(ITEM_LEVEL_MAX, Math.max(1, item.instance?.itemLevel ?? 1));
    // Level 1 sits at the tier's start; the top level stops short of the next tier.
    const reach = (itemLevel - 1) / ITEM_LEVEL_MAX;
    return { discipline, percent: Math.round(tier.start + (tier.before - tier.start) * reach) };
}

/** How many of the highest Martial skills make up Martial Discipline's depth: about a build (a weapon, defense and one more). */
const MARTIAL_DEPTH_SKILLS = 3;
/** Martial Discipline's share from depth; the rest is breadth (#262). */
const MARTIAL_DEPTH_SHARE = 0.7;
/** A skill's highest level. */
const MARTIAL_SKILL_LEVEL_MAX = 10;

/**
 * Martial Discipline (0-100%), mostly depth (#262): 70% from the levels of
 * the three highest Martial skills over their maximum, 30% from every
 * Martial skill's level over theirs (docs/gameplay/skill-tree-ui.md). So a
 * specialist who masters a build stands high without raising every skill,
 * while a single mastered skill is not enough.
 */
export function resolveMartialDisciplinePercent(skills: ReadonlyArray<Pick<Skill, 'id' | 'level'>>): number {
    const martial = SKILL_CATALOG.filter((entry) => entry.branch === 'martial');
    if (martial.length === 0) {
        return 0;
    }
    const levelById = new Map(skills.map((skill) => [skill.id, skill.level]));
    // Widened to number: the levels are summed, and a sum is no skill level.
    const levels: number[] = martial.map((entry) => levelById.get(entry.id) ?? 0).sort((a, b) => b - a);
    const depthSkills = Math.min(MARTIAL_DEPTH_SKILLS, levels.length);
    const depth = levels.slice(0, depthSkills).reduce((sum, level) => sum + level, 0) / (MARTIAL_SKILL_LEVEL_MAX * depthSkills);
    const breadth = levels.reduce((sum, level) => sum + level, 0) / (MARTIAL_SKILL_LEVEL_MAX * levels.length);
    return Math.round((MARTIAL_DEPTH_SHARE * depth + (1 - MARTIAL_DEPTH_SHARE) * breadth) * 100);
}

/** The share of an item's bonuses a character gets from it: all of them at or above its requirement, in proportion below it. */
export function resolveGearPotency(item: GearRequirementSource, disciplines: CharacterDisciplines): number {
    const { discipline, percent } = resolveGearRequirement(item);
    const standing = discipline === 'martial-discipline' ? disciplines.martialDiscipline : disciplines.veilAttunement;
    if (percent <= 0 || standing >= percent) {
        return 1;
    }
    return Math.max(MIN_GEAR_POTENCY, Math.round((standing / percent) * 100) / 100);
}
